import { useEffect, useState } from 'react'

import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { useRenderToasters } from 'hooks/useRenderToasters'
import {
  CREATED_BY_RETAILER_FILTER_KEY,
  RETAILER_CREATED_ORDERS_KEY,
} from 'store/news/constants'

import {
  ORDER_LIST_PAGE,
  REMOVE_STYLE_COLORS_WITH_NO_QUANTITIES_PAGE,
} from 'pages/OrderListPage/constants'
import { WORKSPACE_LIST } from 'routes/paths'

import messages from './OrderListPage.messages'
import styles from './OrderListPage.module.css'
import NewsBanner from 'containers/NewsBanner'
import OrderList from 'containers/OrderList'
import RetailerOrderList from 'containers/OrderList/RetailerOrderList.container'
import RemoveStyleColorsWithoutQuantities from 'containers/RemoveStyleColorsWithoutQuantities'
import { CTABanner } from 'features/Contentful/CTABanner/CTABanner'
import { VisibleInFieldsValues } from 'features/Contentful/CTABanner/constants'
import { ListView } from 'features/Orders/ListView/ListView'
import { LIST_TABS } from 'features/Orders/ListView/listView.hooks'
import { useShouldOfferVA } from 'features/VisualAssortment/hooks/useShouldOfferVA'

const newsToDisplay = [RETAILER_CREATED_ORDERS_KEY]
const CREATED = 'CREATED'
const DESCENDING = 'DESCENDING'

const OrderListPage = (props) => {
  const [currentPage, onChangeCurrentPage] = useState(
    props.location?.state?.currentPage || ORDER_LIST_PAGE,
  )
  const navigate = useNavigate()
  const location = useLocation()

  const { joorPayUseReach } = useFlags()

  const shouldOfferVA = useShouldOfferVA()

  const { renderToast } = useRenderToasters()

  const tab = new URLSearchParams(location.search).get('tab')

  const getRetailerCreatedOrdersBannerMessage = () => {
    const { newRetailerOrdersCount } = props
    return messages[RETAILER_CREATED_ORDERS_KEY](newRetailerOrdersCount)
  }

  const showRetailerCreatedOrdersBanner = () => {
    const { retailerOrdersCountQueryLoading, newRetailerOrdersCount } = props
    return !retailerOrdersCountQueryLoading && newRetailerOrdersCount !== 0
  }

  const handleClickOnRetailerCreatedOrdersBanner = () => {
    const {
      setAppliedFiltersToCreatedByRetailers,
      handleResetSelectedOrders,
      dismissTimestampedNews,
      sortOrders,
      toggleSelectFilter,
      toggleDeselectAllFilters,
    } = props

    toggleDeselectAllFilters()
    toggleSelectFilter({ dataKey: 'createdByRetailer' })
    handleResetSelectedOrders()
    dismissTimestampedNews(CREATED_BY_RETAILER_FILTER_KEY)
    dismissTimestampedNews(RETAILER_CREATED_ORDERS_KEY)
    sortOrders([{ field: CREATED, order: DESCENDING }])
    setAppliedFiltersToCreatedByRetailers()
  }

  useEffect(() => {
    if (!location.state?.toastMessage) {
      return
    }
    renderToast(location.state.toastMessage)

    navigate(
      {
        ...location,
        state: { ...location.state, toastMessage: undefined },
      },
      {
        replace: true,
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderMainContent = () => {
    const { isRetailer, isProBrand } = props

    const commonProps = {
      referral: props.location?.state?.referral,
      onChangeCurrentPage,
    }

    switch (currentPage) {
      case REMOVE_STYLE_COLORS_WITH_NO_QUANTITIES_PAGE:
        return <RemoveStyleColorsWithoutQuantities {...commonProps} />
      case ORDER_LIST_PAGE:
      default:
        if (isRetailer) {
          return <RetailerOrderList {...commonProps} />
        }

        const orderListComponent = <OrderList {...commonProps} />

        if (joorPayUseReach && isProBrand) {
          const initialTab =
            tab === 'joorPayOrders' ? LIST_TABS[1] : LIST_TABS[0]
          return (
            <ListView
              allOrdersContent={orderListComponent}
              initialTab={initialTab}
            />
          )
        }

        return orderListComponent
    }
  }

  if (props.isProRetailer) {
    return <Navigate to={WORKSPACE_LIST} />
  }

  // TODO(sperez): ch39778 move retailer created orders banner out of news

  const isNewsBannerVisible = showRetailerCreatedOrdersBanner()

  return (
    <>
      {isNewsBannerVisible && (
        <NewsBanner
          newsToDisplay={newsToDisplay}
          dynamicMessages={{
            [RETAILER_CREATED_ORDERS_KEY]:
              getRetailerCreatedOrdersBannerMessage(),
          }}
          onClickOnText={handleClickOnRetailerCreatedOrdersBanner}
        />
      )}
      <div
        className={classNames(styles.bannerSlot, {
          [styles.flush]: !isNewsBannerVisible,
        })}
      >
        {shouldOfferVA && (
          <CTABanner visibleIn={VisibleInFieldsValues.OrderList} />
        )}
      </div>
      {renderMainContent()}
    </>
  )
}

OrderListPage.propTypes = {
  newRetailerOrdersCount: PropTypes.number,
  anyRetailerCreatedOrders: PropTypes.bool,
  retailerOrdersCountQueryLoading: PropTypes.bool,
  setAppliedFiltersToCreatedByRetailers: PropTypes.func,
  handleResetSelectedOrders: PropTypes.func,
  dismissTimestampedNews: PropTypes.func,
  sortOrders: PropTypes.func,
  toggleSelectFilter: PropTypes.func,
  toggleDeselectAllFilters: PropTypes.func,
  isProRetailer: PropTypes.bool,
  isRetailer: PropTypes.bool,
}
OrderListPage.defaultProps = {
  newRetailerOrdersCount: 0,
  anyRetailerCreatedOrders: false,
  retailerOrdersCountQueryLoading: false,
  setAppliedFiltersToCreatedByRetailers: () => {},
  handleResetSelectedOrders: () => {},
  dismissTimestampedNews: () => {},
  sortOrders: () => {},
  toggleSelectFilter: () => {},
  toggleDeselectAllFilters: () => {},
  isProRetailer: false,
  isRetailer: false,
}

export default OrderListPage
